import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw } from 'lucide-react';
import { format, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { useShiftSwapValidation } from '../hooks/useShiftSwapValidation';

interface ShiftSwapRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShiftSwapRequestDialog = ({ open, onOpenChange }: ShiftSwapRequestDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { validateSwap } = useShiftSwapValidation();
  const [myShiftId, setMyShiftId] = useState<string>('');
  const [targetGuardId, setTargetGuardId] = useState<string>('');
  const [targetShiftId, setTargetShiftId] = useState<string>('');

  const { data: myShifts, isLoading: isLoadingMyShifts } = useQuery({
    queryKey: ['my-available-shifts'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const now = new Date();
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

      const { data, error } = await supabase
        .from('team_schedules')
        .select('*')
        .contains('assigned_guards', [user.id])
        .gte('start_date', weekStart.toISOString())
        .lte('start_date', weekEnd.toISOString())
        .order('start_date');

      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const { data: otherGuards, isLoading: isLoadingGuards } = useQuery({
    queryKey: ['other-guards', myShiftId],
    queryFn: async () => {
      if (!myShiftId) return [];

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const myShift = myShifts?.find(s => s.id === myShiftId);
      if (!myShift) return [];

      const weekStart = startOfWeek(parseISO(myShift.start_date), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(parseISO(myShift.start_date), { weekStartsOn: 1 });

      console.log('Fetching shifts in week:', { weekStart, weekEnd });
      const { data: shiftsInWeek, error: shiftsError } = await supabase
        .from('team_schedules')
        .select('assigned_guards')
        .gte('start_date', weekStart.toISOString())
        .lte('start_date', weekEnd.toISOString());

      if (shiftsError) {
        console.error('Error fetching shifts:', shiftsError);
        throw shiftsError;
      }

      console.log('Shifts in week:', shiftsInWeek);

      const guardIds = new Set<string>();
      shiftsInWeek?.forEach(shift => {
        shift.assigned_guards?.forEach((guardId: string) => {
          if (guardId !== user.id) guardIds.add(guardId);
        });
      });

      console.log('Guard IDs found:', Array.from(guardIds));

      if (guardIds.size === 0) return [];

      const { data: guards, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', Array.from(guardIds));

      if (error) {
        console.error('Error fetching guards:', error);
        throw error;
      }
      
      console.log('Guards found:', guards);
      return guards || [];
    },
    enabled: !!myShiftId && open && !!myShifts,
  });

  const { data: targetShifts, isLoading: isLoadingTargetShifts } = useQuery({
    queryKey: ['target-shifts', myShiftId, targetGuardId],
    queryFn: async () => {
      if (!myShiftId || !targetGuardId) return [];

      const myShift = myShifts?.find(s => s.id === myShiftId);
      if (!myShift) return [];

      const weekStart = startOfWeek(parseISO(myShift.start_date), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(parseISO(myShift.start_date), { weekStartsOn: 1 });

      console.log('Fetching target shifts for guard:', targetGuardId);
      const { data, error } = await supabase
        .from('team_schedules')
        .select('*')
        .contains('assigned_guards', [targetGuardId])
        .gte('start_date', weekStart.toISOString())
        .lte('start_date', weekEnd.toISOString())
        .order('start_date');

      if (error) {
        console.error('Error fetching target shifts:', error);
        throw error;
      }
      
      console.log('Target shifts found:', data);
      return data || [];
    },
    enabled: !!myShiftId && !!targetGuardId && open && !!myShifts,
  });

  const createRequestMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !myShiftId || !targetGuardId || !targetShiftId) {
        throw new Error('Missing required fields');
      }

      const myShift = myShifts?.find(s => s.id === myShiftId);
      const targetShift = targetShifts?.find(s => s.id === targetShiftId);

      if (!myShift || !targetShift) {
        throw new Error('Shifts not found');
      }

      // Validate swap
      const validation = await validateSwap(myShift, targetShift, user.id, targetGuardId);
      if (!validation.isValid) {
        throw new Error(validation.reason);
      }

      const { error } = await supabase
        .from('shift_swap_requests')
        .insert({
          requester_guard_id: user.id,
          requester_shift_id: myShiftId,
          target_guard_id: targetGuardId,
          target_shift_id: targetShiftId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-swap-requests'] });
      toast({
        title: 'Swap request created',
        description: 'Your shift swap request has been submitted.',
      });
      onOpenChange(false);
      setMyShiftId('');
      setTargetGuardId('');
      setTargetShiftId('');
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create swap request',
        variant: 'destructive',
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Request Shift Swap</DialogTitle>
          <DialogDescription>
            Select your shift and the shift you want to swap with
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Your Shift</label>
            {isLoadingMyShifts ? (
              <div className="flex items-center justify-center py-4">
                <RefreshCw className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <Select value={myShiftId} onValueChange={setMyShiftId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your shift" />
                </SelectTrigger>
                <SelectContent>
                  {myShifts?.map(shift => (
                    <SelectItem key={shift.id} value={shift.id}>
                      {format(parseISO(shift.start_date), 'PPp')} - {format(parseISO(shift.end_date), 'p')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {myShiftId && (
            <div>
              <label className="text-sm font-medium mb-2 block">Target Guard</label>
              {isLoadingGuards ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                </div>
              ) : (
                <Select value={targetGuardId} onValueChange={setTargetGuardId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select guard" />
                  </SelectTrigger>
                  <SelectContent>
                    {otherGuards?.map(guard => (
                      <SelectItem key={guard.id} value={guard.id}>
                        {guard.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {targetGuardId && (
            <div>
              <label className="text-sm font-medium mb-2 block">Target Shift</label>
              {isLoadingTargetShifts ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                </div>
              ) : (
                <Select value={targetShiftId} onValueChange={setTargetShiftId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target shift" />
                  </SelectTrigger>
                  <SelectContent>
                    {targetShifts?.map(shift => (
                      <SelectItem key={shift.id} value={shift.id}>
                        {format(parseISO(shift.start_date), 'PPp')} - {format(parseISO(shift.end_date), 'p')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createRequestMutation.mutate()}
              disabled={!myShiftId || !targetGuardId || !targetShiftId || createRequestMutation.isPending}
            >
              {createRequestMutation.isPending && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}
              Create Request
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
